/* Stone Haus cladding buy box — Sep 26, 2026
   Sep 29, 2026: "I know my m²" tab takes corner pieces as one lineal-metre field (data-shc-corner-lm).
   The "Wall size calculator" tab still works them out from corners × height. */
(() => {
  if (window.__shcBuyboxBooted) return;
  window.__shcBuyboxBooted = true;

  const SAMPLE_KEY = 'stonehaus_sample_tray_v1';

  const toNumber = (input) => {
    if (!input) return 0;
    const value = parseFloat(String(input.value || '').replace(',', '.'));
    return Number.isFinite(value) && value > 0 ? value : 0;
  };
  // Round to 3dp first so floating point noise (7.92000001) doesn't bump a whole unit.
  const ceilQty = (value) => Math.ceil(Math.round(value * 1000) / 1000);
  const trimNum = (value) => String(Math.round(value * 100) / 100);

  const money = (cents, currency) => {
    const amount = Math.round(cents) / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency || 'AUD',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(amount % 1 === 0 ? 0 : 2);
    }
  };

  const sydneyWeekday = () => {
    try {
      return new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', weekday: 'short' }).format(new Date());
    } catch (error) {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date().getDay()];
    }
  };

  const readSamples = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(SAMPLE_KEY) || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch (error) {
      return [];
    }
  };

  function init(root) {
    if (!root || root.dataset.shcReady === 'true') return;
    root.dataset.shcReady = 'true';

    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => Array.from(root.querySelectorAll(selector));

    let variants = {};
    try { variants = JSON.parse(q('[data-shc-variants]').textContent); } catch (error) { variants = {}; }
    const walling = variants.walling || null;
    const corners = variants.corners && variants.corners.available ? variants.corners : null;

    const waste = Number(root.dataset.waste) || 0;
    const afterpayLimit = Number(root.dataset.afterpayLimit) || 4000;
    const currency = root.dataset.currency || 'AUD';

    const el = {
      tabs: qa('[data-shc-tab]'),
      panels: qa('[data-shc-panel]'),
      area: q('[data-shc-area]'),
      width: q('[data-shc-width]'),
      height: q('[data-shc-height]'),
      wasteToggle: q('[data-shc-waste-toggle]'),
      cornerToggle: q('[data-shc-corner-toggle]'),
      cornerFields: q('[data-shc-corner-fields]'),
      cornerModes: qa('[data-shc-corner-mode]'),
      cornerLm: q('[data-shc-corner-lm]'),
      cornerCount: q('[data-shc-corner-count]'),
      cornerHeight: q('[data-shc-corner-height]'),
      summary: q('[data-shc-summary]'),
      wallLabel: q('[data-shc-wall-label]'),
      wallQty: q('[data-shc-wall-qty]'),
      wallCost: q('[data-shc-wall-cost]'),
      cornerRow: q('[data-shc-corner-row]'),
      cornerLabel: q('[data-shc-corner-label]'),
      cornerQty: q('[data-shc-corner-qty]'),
      cornerCost: q('[data-shc-corner-cost]'),
      total: q('[data-shc-total]'),
      afterpay: q('[data-shc-afterpay]'),
      atc: q('[data-shc-atc]'),
      atcLabel: q('[data-shc-atc-label]'),
      buyNow: q('[data-shc-buynow]'),
      error: q('[data-shc-error]'),
      exampleToggle: q('[data-shc-example-toggle]'),
      enquiry: q('[data-shc-enquiry]'),
      sampleBtn: q('[data-shc-sample-btn]'),
      ready: q('[data-shc-ready]'),
      copy: q('[data-shc-copy]')
    };

    const afterpayDefault = el.afterpay ? el.afterpay.innerHTML : '';
    let mode = 'area';
    let cornerHeightTouched = false;
    let state = { wallQty: 0, cornerQty: 0, total: 0 };

    // Old theme markup has no single lineal-metre field; fall back to corners x height there.
    const cornerMode = () => (mode === 'area' && el.cornerLm ? 'lm' : 'size');
    const syncCornerMode = () => {
      el.cornerModes.forEach((box) => { box.hidden = box.dataset.shcCornerMode !== mode; });
    };

    /* ---------- Tabs ---------- */
    const setMode = (next, focus) => {
      if (next === mode) return;
      // Carry the measurement across so switching tabs never loses work.
      if (next === 'area' && el.area && !toNumber(el.area)) {
        const sized = toNumber(el.width) * toNumber(el.height);
        if (sized > 0) el.area.value = trimNum(sized);
      }
      if (next === 'area' && el.cornerLm && !toNumber(el.cornerLm)) {
        const run = Math.floor(toNumber(el.cornerCount)) * toNumber(el.cornerHeight);
        if (run > 0) el.cornerLm.value = trimNum(run);
      }
      mode = next;
      el.tabs.forEach((tab) => {
        const active = tab.dataset.shcTab === mode;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      el.panels.forEach((panel) => { panel.hidden = panel.dataset.shcPanel !== mode; });
      syncCornerMode();
      if (mode === 'size') syncCornerHeight();
      update();
    };

    el.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setMode(tab.dataset.shcTab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const nextIndex = (index + (event.key === 'ArrowRight' ? 1 : -1) + el.tabs.length) % el.tabs.length;
        setMode(el.tabs[nextIndex].dataset.shcTab, true);
      });
    });

    /* ---------- Corners ---------- */
    const syncCornerHeight = () => {
      if (!el.cornerHeight || cornerHeightTouched) return;
      const wallHeight = toNumber(el.height);
      if (wallHeight > 0) el.cornerHeight.value = trimNum(wallHeight);
    };

    if (el.cornerToggle) {
      el.cornerToggle.addEventListener('change', () => {
        if (el.cornerFields) el.cornerFields.hidden = !el.cornerToggle.checked;
        if (el.cornerToggle.checked) {
          if (cornerMode() === 'lm') {
            if (!toNumber(el.cornerLm)) el.cornerLm.focus();
          } else {
            syncCornerHeight();
            if (el.cornerHeight && !toNumber(el.cornerHeight)) el.cornerHeight.focus();
          }
        }
        update();
      });
    }
    if (el.cornerHeight) {
      el.cornerHeight.addEventListener('input', () => { cornerHeightTouched = el.cornerHeight.value !== ''; });
    }
    if (el.height) el.height.addEventListener('input', syncCornerHeight);

    /* ---------- Calculation ---------- */
    const calculate = () => {
      const area = mode === 'area'
        ? toNumber(el.area)
        : toNumber(el.width) * toNumber(el.height);
      const wasteOn = !!(el.wasteToggle && el.wasteToggle.checked && waste > 0);
      const wallQty = area > 0 ? ceilQty(area * (wasteOn ? 1 + waste / 100 : 1)) : 0;

      const cornerInput = cornerMode();
      let cornerLm = 0;
      let cornerCount = 0;
      let cornerHeight = 0;
      let cornerQty = 0;
      if (corners && el.cornerToggle && el.cornerToggle.checked) {
        if (cornerInput === 'lm') {
          cornerLm = toNumber(el.cornerLm);
          cornerQty = cornerLm > 0 ? ceilQty(cornerLm) : 0;
        } else {
          cornerCount = Math.floor(toNumber(el.cornerCount));
          cornerHeight = toNumber(el.cornerHeight);
          cornerLm = cornerCount * cornerHeight;
          cornerQty = cornerCount > 0 && cornerHeight > 0 ? ceilQty(cornerLm) : 0;
        }
      }

      const wallCost = walling ? wallQty * walling.price : 0;
      const cornerCost = corners ? cornerQty * corners.price : 0;
      return { area, wasteOn, wallQty, wallCost, cornerInput, cornerLm, cornerCount, cornerHeight, cornerQty, cornerCost, total: wallCost + cornerCost };
    };

    const cornerSummary = (r) => {
      if (r.cornerInput === 'lm') {
        const rounded = Math.round(r.cornerLm * 1000) !== r.cornerQty * 1000;
        return 'L-shaped corners' + (rounded ? ' · ' + trimNum(r.cornerLm) + ' lm, rounded up' : '');
      }
      return 'Corners · ' + r.cornerCount + ' × ' + trimNum(r.cornerHeight) + ' m, rounded up';
    };

    const permalink = () => {
      const parts = [];
      if (state.wallQty && walling) parts.push(walling.id + ':' + state.wallQty);
      if (state.cornerQty && corners) parts.push(corners.id + ':' + state.cornerQty);
      if (!parts.length) return '#';
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const base = root.endsWith('/') ? root : root + '/';
      const shopPay = el.buyNow && el.buyNow.dataset.shopPay === 'true';
      return base + 'cart/' + parts.join(',') + (shopPay ? '?payment=shop_pay' : '');
    };

    const update = () => {
      const r = calculate();
      state = r;
      const hasQty = r.wallQty > 0 || r.cornerQty > 0;

      if (el.summary) el.summary.hidden = !hasQty;
      if (el.wallLabel) {
        el.wallLabel.textContent = r.wallQty
          ? 'Walling · ' + trimNum(r.area) + ' m²' + (r.wasteOn ? ' + ' + waste + '%' : '') + ', rounded up'
          : 'Walling';
      }
      if (el.wallQty) el.wallQty.textContent = r.wallQty + ' m²';
      if (el.wallCost) el.wallCost.textContent = money(r.wallCost, currency);

      if (el.cornerRow) {
        el.cornerRow.hidden = !(r.cornerQty > 0);
        if (el.cornerLabel) el.cornerLabel.textContent = cornerSummary(r);
        if (el.cornerQty) el.cornerQty.textContent = r.cornerQty + ' lm';
        if (el.cornerCost) el.cornerCost.textContent = money(r.cornerCost, currency);
      }
      if (el.total) el.total.textContent = money(r.total, currency);

      if (el.afterpay) {
        if (!hasQty) {
          el.afterpay.innerHTML = afterpayDefault;
        } else if (r.total / 100 <= afterpayLimit) {
          el.afterpay.innerHTML = 'or 4 payments of <strong>' + money(Math.round(r.total / 4), currency) +
            '</strong> for this wall <span class="shc-dot">·</span> up to ' + money(afterpayLimit * 100, currency);
        } else {
          el.afterpay.innerHTML = 'Afterpay is available on orders up to ' + money(afterpayLimit * 100, currency);
        }
      }

      if (el.atc) {
        const soldOut = !walling || !walling.available;
        el.atc.disabled = soldOut || !hasQty;
        if (soldOut) {
          el.atcLabel.textContent = 'Sold out';
        } else if (!hasQty) {
          el.atcLabel.textContent = el.atc.dataset.defaultLabel || 'Enter your wall size to see your total';
        } else {
          const parts = [];
          if (r.wallQty) parts.push(r.wallQty + ' m²');
          if (r.cornerQty) parts.push(r.cornerQty + ' lm');
          el.atcLabel.textContent = 'Add ' + parts.join(' + ') + ' to cart · ' + money(r.total, currency);
        }
      }

      if (el.buyNow) {
        el.buyNow.href = permalink();
        el.buyNow.classList.toggle('is-disabled', !hasQty);
        el.buyNow.setAttribute('aria-disabled', String(!hasQty));
      }
      if (el.error) el.error.hidden = true;
    };

    root.addEventListener('input', (event) => {
      if (event.target.matches('input[type="number"]')) update();
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-shc-waste-toggle]')) update();
    });

    /* ---------- Add to cart ---------- */
    const showError = (message) => {
      if (!el.error) return;
      el.error.textContent = message || 'Something went wrong. Please try again or call us.';
      el.error.hidden = false;
    };

    const addToCart = async () => {
      const r = calculate();
      const items = [];
      if (r.wallQty > 0 && walling) items.push({ id: walling.id, quantity: r.wallQty });
      if (r.cornerQty > 0 && corners) items.push({ id: corners.id, quantity: r.cornerQty });
      if (!items.length) return;

      const drawer = document.querySelector('cart-drawer');
      const useDrawer = !!(drawer && drawer.dataset.cartType === 'drawer' && typeof drawer.renderContents === 'function');
      const sections = useDrawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id)
        : [];

      el.atc.disabled = true;
      el.atc.classList.add('is-loading');
      const previousLabel = el.atcLabel.textContent;
      el.atcLabel.textContent = 'Adding…';

      try {
        const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const base = rootPath.endsWith('/') ? rootPath : rootPath + '/';
        const body = { items };
        if (sections.length) {
          body.sections = sections.join(',');
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(base + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'We could not add that to your cart.');
        }

        try {
          // eslint-disable-next-line no-undef
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shc-buybox', cartData: data });
        } catch (error) {}

        if (useDrawer && data.sections) {
          try {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data, !document.body.classList.contains('page-overlay-cart-on'));
          } catch (error) {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }
        el.atcLabel.textContent = 'Added to cart ✓';
        setTimeout(update, 1800);
      } catch (error) {
        el.atcLabel.textContent = previousLabel;
        showError(error.message);
        el.atc.disabled = false;
      } finally {
        el.atc.classList.remove('is-loading');
      }
    };

    if (el.atc) el.atc.addEventListener('click', addToCart);
    if (el.buyNow) {
      el.buyNow.addEventListener('click', (event) => {
        if (el.buyNow.classList.contains('is-disabled')) event.preventDefault();
      });
    }

    /* ---------- Help links ---------- */
    if (el.exampleToggle) {
      el.exampleToggle.addEventListener('click', () => {
        const panel = document.getElementById(el.exampleToggle.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !panel.hidden;
        el.exampleToggle.setAttribute('aria-expanded', String(!panel.hidden));
      });
    }

    if (el.enquiry) {
      el.enquiry.addEventListener('click', (event) => {
        const panel = document.querySelector('[id*="stonehaus_enquiry_panel"]');
        event.preventDefault();
        if (!panel) {
          window.location.href = '/pages/contact';
          return;
        }
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const message = panel.querySelector('textarea');
        if (message) {
          const r = calculate();
          if (!message.value && r.wallQty) {
            const title = (document.querySelector('.shc-title') || {}).textContent || 'this stone';
            let cornerLine = '';
            if (r.cornerQty) {
              cornerLine = r.cornerInput === 'lm'
                ? '\nL-shaped corner pieces: ' + trimNum(r.cornerLm) + ' lm'
                : '\nExternal corners: ' + r.cornerCount + ' × ' + trimNum(r.cornerHeight) + ' m';
            }
            message.value = 'Stone: ' + title.trim() + '\nApprox. area: ' + trimNum(r.area) + ' m²' + cornerLine + '\n';
          }
          setTimeout(() => { try { message.focus({ preventScroll: true }); } catch (error) {} }, 500);
        }
      });
    }

    /* ---------- Fulfilment options ---------- */
    const options = qa('[data-shc-opt]');
    const syncOptions = () => {
      options.forEach((option) => {
        const radio = option.querySelector('input[type="radio"]');
        option.classList.toggle('is-active', !!(radio && radio.checked));
      });
    };
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-shc-opt] input[type="radio"]')) syncOptions();
    });
    options.forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');
      option.addEventListener('focusin', (event) => {
        if (radio && !radio.checked && event.target !== radio && option.contains(event.target)) {
          radio.checked = true;
          syncOptions();
        }
      });
      option.addEventListener('click', (event) => {
        if (radio && !radio.checked && !event.target.closest('a, button, input')) {
          radio.checked = true;
          syncOptions();
        }
      });
    });

    const estimator = q('.sh-delivery-estimator');
    if (estimator) {
      const button = estimator.querySelector('[data-sh-delivery-submit]');
      const input = estimator.querySelector('[data-sh-delivery-postcode]');
      if (button) button.textContent = 'Check';
      if (input) input.setAttribute('placeholder', 'Postcode, e.g. 2170');
    }

    if (el.ready) {
      el.ready.textContent = sydneyWeekday() === 'Sat' ? 'Monday' : 'Tomorrow';
    }

    /* ---------- Samples ---------- */
    const syncSample = () => {
      if (!el.sampleBtn) return;
      const handle = root.dataset.handle;
      const inTray = readSamples().some((sample) => sample && (sample.handle === handle));
      el.sampleBtn.classList.toggle('is-added', inTray);
      el.sampleBtn.textContent = inTray ? '✓ In your samples' : 'Add to samples';
    };
    if (el.sampleBtn) {
      syncSample();
      document.addEventListener('click', () => setTimeout(syncSample, 60));
      window.addEventListener('storage', syncSample);
    }

    /* ---------- Copy phone ---------- */
    if (el.copy) {
      el.copy.addEventListener('click', async () => {
        const value = el.copy.dataset.shcCopy || '';
        try {
          await navigator.clipboard.writeText(value);
          el.copy.textContent = 'Copied';
          el.copy.classList.add('is-copied');
          setTimeout(() => { el.copy.textContent = 'Copy'; el.copy.classList.remove('is-copied'); }, 1600);
        } catch (error) {
          window.location.href = 'tel:' + value.replace(/\s+/g, '');
        }
      });
    }

    syncCornerMode();
    update();
  }

  const scan = (scope) => {
    (scope || document).querySelectorAll('[data-shc-buybox]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
